import { useMemo } from 'react'

import {
  GetSublevelCountsDocument,
  JobCatalogPeopleDataCollectionDocument,
  JobCatalogPeopleDataCollectionEmployeeFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import {
  NODE_UUID_TYPES,
  TreeNode,
  getNodeType,
  getSubtreeLevelUuids,
  parseTreeNodes,
} from 'modules/job_catalog/lib/nodesUtils'

export type JobCatalogPerson = JobCatalogPeopleDataCollectionEmployeeFragment

export const isPeopleSourceReady = (isLevel: boolean, levelUuids: string[]) => {
  return isLevel || levelUuids.length > 0
}

export const shouldShowPeopleSourceFallback = ({
  isReady,
  loadingLevels,
}: {
  isReady: boolean
  loadingLevels: boolean
}) => {
  return !isReady && !loadingLevels
}

export const useJobCatalogPeople = (id: string) => {
  const nodeType = useMemo(() => getNodeType(id), [id])
  const isLevel = useMemo(() => nodeType === NODE_UUID_TYPES.LEVEL, [nodeType])

  const {
    data: treeData,
    loading: loadingLevels,
    error: levelsError,
  } = useQuery(GetSublevelCountsDocument, {
    skip: isLevel,
  })

  const levelUuids = useMemo(() => {
    if (isLevel) return [id]
    if (!treeData?.jobCatalog?.treesConnection?.nodes) return []

    const allNodes: TreeNode[] = []
    treeData.jobCatalog.treesConnection.nodes.forEach(
      (tree: { id: string; serializedTree: string }) => {
        allNodes.push(...parseTreeNodes(tree))
      }
    )

    return getSubtreeLevelUuids(allNodes, id)
  }, [isLevel, treeData, id])

  return {
    isLevel,
    levelUuids,
    loadingLevels,
    levelsError,
  }
}

export const useJobCatalogPeopleSource = (id: string, levelUuids: string[]) => {
  const { company } = useCurrentSession()

  return useGraphqlDataCollectionSource(
    {
      document: JobCatalogPeopleDataCollectionDocument,
      perPage: 25,
      totalItemSummary: (totalItems: number) =>
        i18n.tp('job_catalog.nodes.people.total_item_summary', { count: totalItems }),
      mapData: (data) => data.employees.employeesConnection.nodes,
      mapVariables: () => ({
        companyId: company.id,
        referenceContractTreeLevelUuids: levelUuids,
        excludeFullyDeparted: true,
      }),
      mapPagination: (data) => ({
        total: data.employees.employeesConnection.totalCount ?? 0,
      }),
      itemUrl: (item) => `/employees/${item.id}`,
    },
    [id, levelUuids, company.id]
  )
}
